// Frontend/src/pages/resident/screens/PaymentsPageNew.tsx
import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogOverlay,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CalendarDays,
  CheckCircle2,
  CreditCard,
  FileUp,
  Info,
  ReceiptText,
  Copy,
  Check,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

// Import API
import {
  getMyInvoices,
  markInvoiceAsPaid,
  type Invoice,
  type PaymentMethod,
  getBCVRate,
  getInvoicePaymentDetails,
  downloadInvoice,
  todayInCaracas,
  type InvoicePaymentDetails,
} from "@/api";
import { CommissionBreakdown } from "@/components/CommissionBreakdown";
import { PaymentDetailsCard } from "@/components/PaymentDetailsCard";

/* ---------------- Currency Helpers ---------------- */
function fmtMoneyVes(n: number) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n) + ' Bs.';
}

function DualCurrency({ usd, bcvRate, bcvDate, compact = false }: { usd: number; bcvRate?: number; bcvDate?: string; compact?: boolean }) {
  const { t } = useLanguage();
  // Use a fallback for date formatting if fmtDateTime isn't available
  const formatDate = (dateStr: string) => {
    try { 
      return new Date(dateStr).toLocaleString('en-US', {
        timeZone: 'America/Caracas',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }); 
    } catch(e) { return dateStr; }
  };

  const usdNum = typeof usd === "string" ? parseFloat(usd) : usd;
  const usdFormatted = `$${usdNum.toFixed(2)}`;
  const vesAmount = bcvRate ? usdNum * bcvRate : null;
  
  if (compact) {
    return (
      <span className="inline-flex flex-wrap items-baseline gap-1">
        <span className="font-semibold">{usdFormatted}</span>
        {vesAmount !== null && (
          <span className="text-xs text-gray-500" title={bcvDate ? t("payments.rateValidAsOf", { date: formatDate(bcvDate), rate: bcvRate?.toString() || "" }) : undefined}>
            ({fmtMoneyVes(vesAmount)}) {bcvDate && <span> {t("payments.bcvRateCompact", { date: formatDate(bcvDate), rate: bcvRate?.toString() || "" })}</span>}
          </span>
        )}
      </span>
    );
  }
  
  return (
    <div className="flex flex-col">
      <span className="font-semibold">{usdFormatted}</span>
      {vesAmount !== null && (
        <span className="text-xs text-gray-500" title={bcvDate ? t("payments.rateValidAsOf", { date: formatDate(bcvDate), rate: bcvRate?.toString() || "" }) : undefined}>
          {fmtMoneyVes(vesAmount)} {bcvDate && <span className="block text-[10px] text-gray-400 mt-0.5">{t("payments.bcvRateNormal", { date: formatDate(bcvDate), rate: bcvRate?.toString() || "" })}</span>}
        </span>
      )}
    </div>
  );
}

/* ---------------- Helpers ---------------- */

type ResidentInvoiceStateKey =
  | "UNPAID"
  | "OVERDUE"
  | "PARTIALLY_PAID"
  | "PENDING_VERIFICATION"
  | "PENDING_HOA_TRANSFER"
  | "PAID_WAITING_HOA"
  | "PAID_AND_CONFIRMED";

type ResidentInvoiceState = {
  key: ResidentInvoiceStateKey;
  label: string;      // badge text
  badgeClass: string; // classes for the badge
  railClass: string;  // classes for Card railColor
  subtitle?: string;  // optional extra line
};

function getDirectHoaResidentCopy(language: string) {
  if (language === "es") {
    return {
      reviewing: "La administración está revisando tu comprobante de pago.",
      confirmed: "La administración confirmó que recibió tu pago.",
      verifyingCard: "Tu pago está siendo verificado por la administración.",
      timelineSent: "Comprobante enviado a la administración",
      timelineConfirmed: "La administración confirmó tu pago",
      statusSent: "Pago enviado (en revisión HOA)",
      statusConfirmed: "Pago confirmado",
    };
  }

  return {
    reviewing: "The HOA administration is reviewing your payment proof.",
    confirmed: "The HOA administration confirmed they received your payment.",
    verifyingCard: "Your payment is being verified by the HOA administration.",
    timelineSent: "Payment proof sent to HOA administration",
    timelineConfirmed: "HOA administration confirmed your payment",
    statusSent: "Payment sent (HOA review)",
    statusConfirmed: "Payment confirmed",
  };
}

function getResidentInvoiceState(invoice: Invoice, t: (key: string) => string, language: string): ResidentInvoiceState {
  const isOverdue = !!invoice.is_overdue && invoice.status !== "PAID";
  const directCopy = getDirectHoaResidentCopy(language);

  if (invoice.payment_flow === "DIRECT_HOA") {
    if (invoice.status === "PENDING_VERIFICATION") {
      return {
        key: "PENDING_VERIFICATION",
        label: directCopy.statusSent,
        badgeClass: "bg-sky-100 text-sky-700",
        railClass: "border-sky-500",
        subtitle: directCopy.reviewing,
      };
    }

    if (invoice.status === "PAID") {
      return {
        key: "PAID_AND_CONFIRMED",
        label: directCopy.statusConfirmed,
        badgeClass: "bg-emerald-100 text-emerald-700",
        railClass: "border-emerald-500",
        subtitle: directCopy.confirmed,
      };
    }
  }

  // Resident already sent proof to Urbe360
  if (invoice.status === "PENDING_VERIFICATION") {
    return {
      key: "PENDING_VERIFICATION",
      label: t("payments.paymentSent"),
      badgeClass: "bg-sky-100 text-sky-700",
      railClass: "border-sky-500",
      subtitle: t("payments.urbe360Reviewing"),
    };
  }

  // Urbe360 verified and is transferring to HOA
  if (invoice.status === "PENDING_HOA_TRANSFER") {
    return {
      key: "PENDING_HOA_TRANSFER",
      label: t("payments.verified"),
      badgeClass: "bg-blue-100 text-blue-700",
      railClass: "border-blue-500",
      subtitle: t("payments.transferringToHoa"),
    };
  }

  // Urbe360 verified + paid HOA, but HOA hasn't confirmed yet
  if (invoice.status === "PAID" && !invoice.finalized_at) {
    return {
      key: "PAID_WAITING_HOA",
      label: t("payments.paidByUrbe360"),
      badgeClass: "bg-emerald-100 text-emerald-700",
      railClass: "border-emerald-500",
      subtitle: t("payments.urbe360PaidAdmin"),
    };
  }

  // HOA has confirmed receipt
  if (invoice.status === "PAID" && invoice.finalized_at) {
    return {
      key: "PAID_AND_CONFIRMED",
      label: t("payments.paymentConfirmed"),
      badgeClass: "bg-emerald-100 text-emerald-700",
      railClass: "border-emerald-500",
      subtitle: t("payments.adminConfirmedPayment"),
    };
  }

  // Partially paid — HOA recorded part of the balance, remainder outstanding
  if (invoice.status === "PARTIALLY_PAID") {
    return {
      key: "PARTIALLY_PAID",
      label: t("payments.partiallyPaid"),
      badgeClass: "bg-amber-100 text-amber-700",
      railClass: "border-amber-500",
      subtitle: t("payments.partiallyPaidSubtitle"),
    };
  }

  // Unpaid and overdue
  if (isOverdue && (invoice.status === "UNPAID" || invoice.status === "SENT")) {
    return {
      key: "OVERDUE",
      label: t("payments.expired"),
      badgeClass: "bg-rose-100 text-rose-700",
      railClass: "border-rose-500",
      subtitle: t("payments.invoiceOverdue"),
    };
  }

  // Default: unpaid but not overdue yet
  return {
    key: "UNPAID",
    label: t("payments.pending"),
    badgeClass: "bg-amber-100 text-amber-700",
    railClass: "border-amber-500",
    subtitle: t("payments.notReceived"),
  };
}


function getResolvedPaymentMethods(details: InvoicePaymentDetails["resident_payment_details"]): PaymentMethod[] {
  if (!details) return [];

  const methods: PaymentMethod[] = [];
  if (details.zelle_email || details.zelle_phone) methods.push("ZELLE");
  if (
    details.bank_name ||
    details.bank_account_number ||
    details.bank_routing_number ||
    details.account_holder_name
  ) {
    methods.push("BANK_TRANSFER");
  }
  if (
    details.pago_movil_bank ||
    details.pago_movil_phone ||
    details.pago_movil_id_number
  ) {
    methods.push("PAGO_MOVIL");
  }
  if (details.cash_instructions) methods.push("CASH");
  if (details.paypal_email) methods.push("STRIPE");

  return methods;
}

function getResolvedAccountLines(
  method: PaymentMethod,
  details: InvoicePaymentDetails["resident_payment_details"],
  t: (key: string) => string
): { k: string; v: string }[] {
  if (!details) return [];
  const unavailableValue = "—";

  const methodLines: Record<PaymentMethod, { k: string; v: string }[]> = {
    ZELLE: [
      { k: t("payments.recipient"), v: details.zelle_email || details.zelle_phone || unavailableValue },
      { k: t("common.name"), v: details.account_holder_name || unavailableValue },
      { k: t("payments.memo"), v: t("payments.unitInvoice") },
    ],
    BANK_TRANSFER: [
      { k: t("payments.bank"), v: details.bank_name || unavailableValue },
      { k: t("payments.accountName"), v: details.account_holder_name || unavailableValue },
      { k: t("payments.accountNumber"), v: details.bank_account_number || unavailableValue },
      { k: t("payments.routingNumber"), v: details.bank_routing_number || unavailableValue },
      { k: t("payments.reference"), v: t("payments.unitInvoice") },
    ],
    PAGO_MOVIL: [
      { k: t("payments.bank"), v: details.pago_movil_bank || unavailableValue },
      { k: t("common.phone"), v: details.pago_movil_phone || unavailableValue },
      { k: t("payments.idNumberRif"), v: details.pago_movil_id_number || unavailableValue },
      { k: t("payments.concept"), v: t("payments.unitInvoice") },
    ],
    CASH: [
      { k: t("payments.instructions"), v: details.cash_instructions || unavailableValue },
    ],
    STRIPE: [
      { k: "PayPal", v: details.paypal_email || unavailableValue },
    ],
  };

  return methodLines[method];
}

function getPaymentSourceBadgeLabel(
  source: InvoicePaymentDetails["resident_payment_source"],
  language: string
) {
  if (source === "BUILDING") {
    return language === "es" ? "Cuenta del edificio" : "Building account";
  }
  if (source === "HOA_DEFAULT") {
    return language === "es" ? "Cuenta principal HOA" : "HOA main account";
  }
  return language === "es" ? "Sin configurar" : "Not configured";
}

function getPaymentMethodLabel(method: PaymentMethod, t: (key: string) => string) {
  switch (method) {
    case "ZELLE":
      return t("payments.zelle");
    case "BANK_TRANSFER":
      return t("payments.transfer");
    case "PAGO_MOVIL":
      return t("payments.pagoMovil");
    case "CASH":
      return t("payments.cash");
    case "STRIPE":
      return t("payments.stripe");
    default:
      return method;
  }
}

function getResidentPaymentDialogCopy(language: string) {
  if (language === "es") {
    return {
      destinationTitle: "Destino de pago HOA",
      destinationDescription: "Paga esta factura directamente a la cuenta configurada para tu edificio.",
      sourceLabel: "Origen",
      buildingLabel: "Edificio",
      buildingCodeLabel: "Código",
      missingConfigTitle: "Falta la configuración de cobro",
      missingConfigBody:
        "Esta factura no puede recibir comprobantes todavía porque la cuenta bancaria del edificio o la cuenta principal HOA no está configurada.",
      payStepDescription: "Revisa los datos del edificio y envía tu pago directamente a la HOA.",
      paymentProofHelp: "Sube el comprobante y tu identificación para que la administración valide el pago.",
      referenceHelper: "Usa tu unidad y el período de la factura como referencia cuando sea posible.",
      hoaReviewPending: "La administración HOA revisará tu comprobante y te confirmará cuando el pago esté validado.",
      methodUnavailable: "El método seleccionado no está disponible para este edificio.",
    };
  }

  return {
    destinationTitle: "HOA payment destination",
    destinationDescription: "Pay this invoice directly to the account configured for your building.",
    sourceLabel: "Source",
    buildingLabel: "Building",
    buildingCodeLabel: "Code",
    missingConfigTitle: "Payment setup missing",
    missingConfigBody:
      "This invoice cannot accept payment proof yet because the building account or HOA main account has not been configured.",
    payStepDescription: "Review the building payment details and send your payment directly to the HOA.",
    paymentProofHelp: "Upload your payment proof and ID so the HOA administration can validate the payment.",
    referenceHelper: "Use your unit and invoice period as the payment reference whenever possible.",
    hoaReviewPending: "The HOA administration will review your payment proof and confirm the payment once validated.",
    methodUnavailable: "The selected payment method is not available for this building.",
  };
}

/* ---------------- Page Component ---------------- */

export default function PaymentsPageNew() {
  const { t, language } = useLanguage();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Invoice | null>(null);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<"pending" | "history">("pending");

  // Fetch invoices
  const { data: invoices = [], isLoading, refetch } = useQuery({
    queryKey: ["resident", "invoices"],
    queryFn: getMyInvoices,
  });

  // Fetch BCV rate
  const bcvRateQuery = useQuery({
    queryKey: ["bcv-rate"],
    queryFn: () => getBCVRate(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const bcvRate = bcvRateQuery.data?.dollar;

  // `selected` is the card the resident clicked -- a snapshot. Re-resolve it
  // against the list on every render so the dialog shows the invoice's
  // current state rather than its state at click time, and so a payment
  // recorded elsewhere (or by the HOA admin) cannot leave this dialog
  // offering to pay something already settled. Falls back to the snapshot if
  // the invoice has dropped out of the list.
  const selectedInvoice = useMemo(
    () => (selected ? invoices.find((inv) => inv.id === selected.id) ?? selected : null),
    [selected, invoices]
  );

  // Calculate stats
  const stats = useMemo(() => {
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const thisMonthDue = invoices
      .filter((inv) => {
        const due = new Date(inv.due_date);
        return due >= thisMonthStart && due <= now && inv.status !== "PAID";
      })
      .reduce((sum, inv) => sum + parseFloat(inv.total_amount), 0);

    const unpaidTotal = invoices
      .filter((inv) => inv.status !== "PAID")
      .reduce((sum, inv) => sum + parseFloat(inv.total_amount), 0);

    return { thisMonthDue, unpaidTotal };
  }, [invoices]);

  // Filter invoices
  const visibleInvoices = useMemo(() => {
    let list = invoices;

    // Filter by tab
    if (activeTab === "pending") {
      // Show only UNPAID, OVERDUE, and PENDING_VERIFICATION
      list = list.filter((inv) => inv.status !== "PAID");
    } else {
      // Show only PAID invoices
      list = list.filter((inv) => inv.status === "PAID");
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (inv) =>
          inv.id.toLowerCase().includes(q) ||
          inv.period.toLowerCase().includes(q) ||
          (inv.description ?? "").toLowerCase().includes(q)
      );
    }

    // Sort by due date (newest first)
    return [...list].sort((a, b) => +new Date(b.due_date) - +new Date(a.due_date));
  }, [invoices, search, activeTab]);

  return (
    <div className="relative">
      {/* Subtle background */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(31,58,147,0.06),transparent_60%)]" />

      <div className="space-y-6 sm:space-y-7">
        {/* Summary tiles */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <FloatTile
            title={t("payments.dueThisMonth")}
            value={`$${stats.thisMonthDue.toFixed(2)}`}
            state={stats.thisMonthDue > 0 ? "warn" : "ok"}
            hint={t("payments.calendarMonth")}
          />
          <FloatTile
            title={t("payments.totalUnpaid")}
            value={`$${stats.unpaidTotal.toFixed(2)}`}
            state={stats.unpaidTotal > 0 ? "pending" : "ok"}
            hint={`${invoices.filter((i) => i.status !== "PAID").length} ${t("payments.invoice")}(s)`}
          />
        </div>

        {/* Tabs */}
        <div className="flex justify-center">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "pending" | "history")} className="w-full max-w-md">
            <TabsList className="grid w-full grid-cols-2 rounded-2xl p-1 bg-white/70 backdrop-blur-sm ring-1 ring-black/10">
              <TabsTrigger value="pending" className="rounded-xl data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:shadow">
                {t("payments.pendingTab")}
              </TabsTrigger>
              <TabsTrigger value="history" className="rounded-xl data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:shadow">
                {t("payments.historyTab")}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Search bar */}
        <Card variant="glass" className="rounded-3xl p-4 sm:p-5">
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-600">{t("payments.searchInvoices")}</label>
              <input
                type="text"
                placeholder={t("payments.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-black/10 bg-white/90 px-3 py-2 text-sm shadow-sm ring-1 ring-black/5 focus:outline-none focus:ring-2 focus:ring-[rgba(31,58,147,0.35)]"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-600">
                {t("payments.showing")} <span className="font-medium text-gray-900">{visibleInvoices.length}</span> {activeTab === "pending" ? t("payments.invoicesUnpaid") : t("payments.invoicesPaid")}
              </div>
              <Button
                type="button"
                variant="ghost"
                onClick={() => refetch()}
                className="rounded-2xl px-4 py-2 text-sm"
                disabled={isLoading}
              >
                {isLoading ? t("payments.refreshing") : t("payments.refresh")}
              </Button>
            </div>
          </div>
        </Card>

        {/* Invoices list */}
        <div className="space-y-4">
          {isLoading ? (
            <Card variant="glass" className="p-6 rounded-2xl text-sm text-gray-600">
              {t("payments.loadingInvoices")}
            </Card>
          ) : visibleInvoices.length === 0 ? (
            <Card variant="glass" className="p-6 rounded-2xl text-sm text-gray-600">
              {t("payments.noMatchingInvoices")}
            </Card>
          ) : (
            visibleInvoices.map((inv) => (
              <InvoiceCard key={inv.id} invoice={inv} onOpen={() => setSelected(inv)} bcvRate={bcvRate} t={t} language={language} />
            ))
          )}
        </div>

        {/* Invoice detail dialog */}
        {selectedInvoice && (
          <InvoiceDialog
            open={!!selectedInvoice}
            invoice={selectedInvoice}
            onClose={() => setSelected(null)}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ["resident", "invoices"] });
              setSelected(null);
            }}
            bcvRate={bcvRate}
            t={t}
          />
        )}
      </div>
    </div>
  );
}

/* ---------------- UI Components ---------------- */

function FloatTile({
  title,
  value,
  hint,
  state,
}: {
  title: string;
  value: string;
  hint?: string;
  state: "ok" | "warn" | "pending";
}) {
  const tone =
    state === "ok"
      ? "from-emerald-50/90 to-emerald-100/60 text-emerald-800"
      : state === "warn"
        ? "from-rose-50/90 to-rose-100/60 text-rose-800"
        : "from-amber-50/90 to-amber-100/60 text-amber-800";

  return (
    <div
      className={`rounded-2xl p-4 sm:p-5 bg-gradient-to-b ${tone} ring-1 ring-black/5 shadow-lg shadow-black/10 hover:shadow-xl transition-shadow`}
    >
      <div className="text-xs sm:text-sm opacity-80">{title}</div>
      <div className="text-xl sm:text-2xl font-semibold mt-1 tracking-tight">
        {value}
      </div>
      {hint && <div className="text-xs opacity-70 mt-1">{hint}</div>}
    </div>
  );
}

function InvoiceCard({
  invoice,
  onOpen,
  bcvRate,
  t,
  language,
}: {
  invoice: Invoice;
  onOpen: () => void;
  bcvRate?: number;
  t: (key: string) => string;
  language: string;
}) {
  const state = getResidentInvoiceState(invoice, t, language);
  const directCopy = getDirectHoaResidentCopy(language);

  // Date line based on flow state
  let dateText: string;
  if (state.key === "UNPAID" || state.key === "OVERDUE") {
    dateText = `${t("payments.dueOn2")} ${new Date(invoice.due_date).toLocaleDateString()}`;
  } else if (state.key === "PENDING_VERIFICATION") {
    if (invoice.marked_paid_at) {
      dateText = `${t("payments.paymentSentDate")} ${new Date(
        invoice.marked_paid_at
      ).toLocaleDateString()}`;
    } else {
      dateText = `${t("payments.paymentSentDate")} ${t("payments.underReview")}`;
    }
  } else {
    // PAID_WAITING_HOA / PAID_AND_CONFIRMED
    const paidDate =
      invoice.finalized_at || invoice.verified_at || invoice.marked_paid_at || invoice.created_at;
    dateText = paidDate
      ? `${t("payments.paidDate")} ${new Date(paidDate).toLocaleDateString()}`
      : t("payments.paid");
  }

  return (
    <button
      onClick={onOpen}
      className="w-full text-left group focus:outline-none"
      title={t("payments.openInvoice")}
    >
      <Card
        variant="glass"
        hover
        rail
        railColor={state.railClass}
        railWidth="border-l-4"
        className="rounded-3xl p-5 sm:p-6"
      >
        <div className="flex items-start gap-4">
          <div className="rounded-2xl ring-1 ring-black/5 p-2.5 bg-white/80 shadow-sm">
            {state.key === "UNPAID" || state.key === "OVERDUE" ? (
              <CreditCard className="w-5 h-5 text-gray-700" />
            ) : (
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            )}
          </div>

          <div className="flex-1">
            <div className="flex items-start">
              <h3 className="font-semibold text-lg tracking-tight text-gray-900">
                {invoice.period} • {t("common.unit")} {invoice.unit_code}
              </h3>
              <div className="ml-auto text-right">
                <DualCurrency usd={parseFloat(invoice.total_amount)} bcvRate={invoice.bcv_rate_snapshot ? parseFloat(invoice.bcv_rate_snapshot) : bcvRate} bcvDate={invoice.bcv_rate_date} />
                <div className="text-sm text-gray-600">
                  {invoice.status === "PAID"
                    ? invoice.payment_method || t("payments.paid")
                    : t("payments.unpaid")}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-1 text-sm">
              <span className={`px-2 py-1 rounded-full ${state.badgeClass}`}>
                {state.label}
              </span>
              <span className="inline-flex items-center gap-1 text-gray-600">
                <CalendarDays className="w-4 h-4" />
                {dateText}
              </span>
            </div>

            {state.subtitle && (
              <div className="mt-1 text-xs text-gray-600">{state.subtitle}</div>
            )}

            {invoice.description && (
              <p className="text-sm text-gray-700 mt-3 leading-6">
                {invoice.description}
              </p>
            )}

            {state.key === "PENDING_VERIFICATION" && (
              <div className="mt-3 flex items-center gap-2 text-sm text-sky-700 bg-sky-50 px-3 py-2 rounded-xl">
                <AlertCircle className="w-4 h-4" />
                {invoice.payment_flow === "DIRECT_HOA" ? directCopy.verifyingCard : t("payments.verifyingPayment")}
              </div>
            )}
          </div>
        </div>
      </Card>
    </button>
  );
}


function InvoiceDialog({
  open,
  invoice,
  onClose,
  onSuccess,
  bcvRate,
  t,
}: {
  open: boolean;
  invoice: Invoice;
  onClose: () => void;
  onSuccess: () => void;
  bcvRate?: number;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  const { language } = useLanguage();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<"summary" | "pay">("summary");
  const [tab, setTab] = useState<PaymentMethod>("ZELLE");
  const [transactionFile, setTransactionFile] = useState<File | null>(null);
  const [idFile, setIdFile] = useState<File | null>(null);
  // The day the transfer actually left the resident's account. The rate the
  // payment is recorded at is resolved from THIS date, not from the day the
  // proof was uploaded -- someone who paid on Friday and uploads on Monday
  // was being converted at Monday's rate, and the VES moves ~0.5% a day.
  const [paidOn, setPaidOn] = useState<string>(todayInCaracas);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const state = getResidentInvoiceState(invoice, t, language);
  const directCopy = getDirectHoaResidentCopy(language);
  const dialogCopy = getResidentPaymentDialogCopy(language);
  const isDirectFlow = invoice.payment_flow === "DIRECT_HOA";

  const { data: paymentDetails } = useQuery<InvoicePaymentDetails>({
    queryKey: ["invoice-payment-details", invoice.id],
    queryFn: () => getInvoicePaymentDetails(invoice.id),
    enabled: !!invoice.id && open,
  });

  const canPay = invoice.status === "UNPAID" || invoice.status === "SENT";
  const residentPaymentDetails = paymentDetails?.resident_payment_details ?? null;
  const residentPaymentSource = paymentDetails?.resident_payment_source ?? null;
  const residentPaymentConfigStatus =
    paymentDetails?.resident_payment_config_status ?? null;
  const buildingName = paymentDetails?.building_name || invoice.building_name || "—";
  const buildingCode = paymentDetails?.building_code || invoice.building_code || "—";
  const availablePaymentMethods = getResolvedPaymentMethods(residentPaymentDetails);
  const hasConfiguredDestination =
    residentPaymentConfigStatus !== "MISSING" &&
    !!residentPaymentDetails &&
    availablePaymentMethods.length > 0;
  const canStartPayment = canPay && (!isDirectFlow || hasConfiguredDestination);
  const accountLines = getResolvedAccountLines(tab, residentPaymentDetails, t);

  const markAsPaidMutation = useMutation({
    mutationFn: (data: {
      payment_method: PaymentMethod;
      transaction_screenshot: File;
      id_verification: File;
      resident_notes?: string;
      payment_date?: string;
    }) => markInvoiceAsPaid(invoice.id, data),
    onSuccess: async (updated) => {
      // mark_as_paid answers with the invoice as it now stands. Write that
      // into the list before anything refetches, so the card behind this
      // dialog changes state in the same commit as the toast. Leaving it to
      // the refetch alone meant the resident could be told "recibido" while
      // the card underneath still said UNPAID -- the round trip is not
      // guaranteed to be quick, and it is not guaranteed to happen at all if
      // the connection drops between the POST and the GET.
      //
      // Cancel first: a fetch issued before the upload finished would
      // otherwise resolve on top of this and restore the old card.
      await queryClient.cancelQueries({ queryKey: ["resident", "invoices"] });
      queryClient.setQueryData<Invoice[]>(["resident", "invoices"], (previous) =>
        previous?.map((inv) =>
          inv.id === updated.id ? { ...inv, ...updated } : inv
        )
      );
      toast.success(t("payments.paymentMarkedAsPaid"), {
        description: t("payments.paymentBeingVerified"),
      });
      onSuccess();
    },
    onError: (err: any) => {
      toast.error(t("payments.errorMarkingPaid"), {
        description:
          err.response?.data?.detail || t("payments.tryAgainLater"),
      });
    },
  });

  // Keyed on the invoice's ID, not the object. `invoice` is now resolved
  // against the live list on every refresh, so depending on its identity
  // would wipe the half-filled payment form -- uploaded files included --
  // each time the list refetched under the open dialog. What this reset is
  // for is opening a DIFFERENT invoice.
  React.useEffect(() => {
    setStep("summary");
    setTab("ZELLE");
    setTransactionFile(null);
    setIdFile(null);
    setPaidOn(todayInCaracas());
    setNotes("");
    setError(null);
  }, [invoice.id]);

  React.useEffect(() => {
    if (availablePaymentMethods.length > 0 && !availablePaymentMethods.includes(tab)) {
      setTab(availablePaymentMethods[0]);
    }
  }, [availablePaymentMethods, tab]);

  function handleTransactionFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setError(null);
    if (!f) return setTransactionFile(null);

    const okType =
      f.type === "application/pdf" || f.type.startsWith("image/");
    if (!okType) {
      setError(t("payments.uploadPdfOrImage"));
      return setTransactionFile(null);
    }
    if (f.size > 10 * 1024 * 1024) {
      setError(t("payments.fileTooLarge"));
      return setTransactionFile(null);
    }
    setTransactionFile(f);
  }

  function handleIdFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setError(null);
    if (!f) return setIdFile(null);

    const okType =
      f.type === "application/pdf" || f.type.startsWith("image/");
    if (!okType) {
      setError(t("payments.uploadPdfOrImage"));
      return setIdFile(null);
    }
    if (f.size > 10 * 1024 * 1024) {
      setError(t("payments.fileTooLarge"));
      return setIdFile(null);
    }
    setIdFile(f);
  }

  function submitProof(e: React.FormEvent) {
    e.preventDefault();
    if (!canStartPayment) {
      setError(dialogCopy.missingConfigBody);
      return;
    }
    if (!availablePaymentMethods.includes(tab)) {
      setError(dialogCopy.methodUnavailable);
      return;
    }
    if (!transactionFile || !idFile) {
      setError(t("payments.uploadBothFiles"));
      return;
    }

    markAsPaidMutation.mutate({
      payment_method: tab,
      transaction_screenshot: transactionFile,
      id_verification: idFile,
      resident_notes: notes.trim() || undefined,
      payment_date: paidOn || undefined,
    });
  }

  // `key` is the STAGE, not the position. This list grows a row at a time as
  // the payment settles -- sent, then confirmed, then receipted -- and under
  // array-index keys React reuses row 0's DOM for whichever stage happens to
  // be first after the next refresh, so a row can end up carrying another
  // stage's label against its own date.
  const timeline: { key: string; label: string; date: string }[] = [];
  if (invoice.marked_paid_at) {
    timeline.push({
      key: "sent",
      label: isDirectFlow ? directCopy.timelineSent : t("payments.paymentSentToUrbe360"),
      date: invoice.marked_paid_at,
    });
  }
  if (isDirectFlow) {
    const confirmedAt = invoice.finalized_at || invoice.verified_at;
    if (confirmedAt) {
      timeline.push({
        key: "confirmed",
        label: directCopy.timelineConfirmed,
        date: confirmedAt,
      });
    }
  } else if (invoice.verified_at) {
    timeline.push({
      key: "verified",
      label: t("payments.urbe360ConfirmedPaid"),
      date: invoice.verified_at,
    });
  }
  if (!isDirectFlow && invoice.finalized_at) {
    timeline.push({
      key: "receipted",
      label: t("payments.adminConfirmedReceipt"),
      date: invoice.finalized_at,
    });
  }

  const isPendingVerification = state.key === "PENDING_VERIFICATION";
  const isPaidFlow =
    state.key === "PAID_WAITING_HOA" || state.key === "PAID_AND_CONFIRMED";

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : onClose())}>
      <DialogOverlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
      <DialogContent
        className="
          bg-gray-50/95 backdrop-blur-xl ring-1 ring-black/5 shadow-2xl z-50
          w-[min(100vw-24px,900px)] p-4 sm:p-6 rounded-3xl
          h-auto max-h-[85dvh] sm:max-h-[90vh]
          overflow-y-auto block
        "
      >
        <DialogHeader className="mb-4 sm:mb-6">
          <DialogTitle className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900">
            {t("payments.invoice")} • {invoice.period}
          </DialogTitle>
          <DialogDescription className="text-gray-600 text-sm sm:text-base">
            {step === "pay"
              ? isDirectFlow
                ? dialogCopy.payStepDescription
                : t("payments.reviewDetailsAndPay")
              : t("payments.reviewInvoiceDetails")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Summary block */}
          <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden">
            {/* Header: Period & Status */}
            <div className="bg-[#1F3A93]/5 px-4 py-3 flex items-center justify-between border-b border-gray-200/60">
              <div className="flex items-center gap-3">
                <ReceiptText className="w-5 h-5 text-[#1F3A93]" />
                <div className="font-semibold text-[#1F3A93]">{t("payments.invoice")} • {invoice.period}</div>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${state.badgeClass}`}>
                {state.label}
              </span>
            </div>
            <div className="p-4 sm:p-5 flex flex-col gap-5">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pb-5 border-b border-gray-100">
                <div className="space-y-1">
                  <div className="text-xs text-gray-500">{t("payments.invoiceId")}</div>
                  <div className="font-medium text-gray-900">{invoice.id.split("-")[0]}...</div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs text-gray-500">{t("common.unit")}</div>
                  <div className="font-medium text-gray-900">{invoice.unit_code}</div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs text-gray-500">{t("payments.dueDate2")}</div>
                  <div className="font-medium text-gray-900">{new Date(invoice.due_date).toLocaleDateString()}</div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs text-gray-500">{t("payments.totalToPay")}</div>
                  <div className="font-bold text-[#1F3A93]">${parseFloat(invoice.total_amount).toFixed(2)}</div>
                </div>
              </div>
              {invoice.description && (
                <div className="text-sm">
                  <span className="font-medium text-gray-600 block mb-1">{t("common.description")}:</span>
                  <span className="text-gray-800">{invoice.description}</span>
                </div>
              )}
              <div className="bg-gray-50/50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">{t("payments.baseAmount")}</span>
                  <span className="font-medium"><DualCurrency usd={parseFloat(invoice.base_amount)} bcvRate={invoice.bcv_rate_snapshot ? parseFloat(invoice.bcv_rate_snapshot) : bcvRate} bcvDate={invoice.bcv_rate_date} /></span>
                </div>
                {parseFloat(invoice.credits) > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">{t("payments.credits")}</span>
                    <span className="font-medium text-emerald-600">-<DualCurrency usd={parseFloat(invoice.credits)} bcvRate={invoice.bcv_rate_snapshot ? parseFloat(invoice.bcv_rate_snapshot) : bcvRate} bcvDate={invoice.bcv_rate_date} compact /></span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <span className="font-semibold text-gray-700">{t("payments.totalToPay")}</span>
                  <div className="text-right">
                    <div className="text-xl font-bold text-[#1F3A93]">
                      ${parseFloat(invoice.total_amount).toFixed(2)}
                    </div>
                    {bcvRate && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        Bs. {(parseFloat(invoice.total_amount) * bcvRate).toLocaleString(
                          "es-VE",
                          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {timeline.length > 0 && (
                <div className="pt-2">
                  <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-3">{t("payments.paymentTimeline")}</h4>
                  <div className="space-y-3">
                    {timeline.map((item) => (
                      <div key={item.key} className="flex justify-between items-center text-sm bg-white p-3 rounded-lg border border-gray-100">
                        <span className="text-gray-700 font-medium">{item.label}</span>
                        <span className="text-gray-500 bg-gray-50 px-2 py-1 rounded">{new Date(item.date).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {isDirectFlow && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    {dialogCopy.destinationTitle}
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    {dialogCopy.destinationDescription}
                  </div>
                </div>
                <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                  {getPaymentSourceBadgeLabel(residentPaymentSource, language)}
                </span>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl bg-slate-50 px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {dialogCopy.buildingLabel}
                  </div>
                  <div className="mt-1 text-sm font-medium text-slate-900">
                    {buildingName}
                  </div>
                </div>
                <div className="rounded-xl bg-slate-50 px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {dialogCopy.buildingCodeLabel}
                  </div>
                  <div className="mt-1 text-sm font-medium text-slate-900">
                    {buildingCode}
                  </div>
                </div>
                <div className="rounded-xl bg-slate-50 px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {dialogCopy.sourceLabel}
                  </div>
                  <div className="mt-1 text-sm font-medium text-slate-900">
                    {getPaymentSourceBadgeLabel(residentPaymentSource, language)}
                  </div>
                </div>
              </div>
              {!hasConfiguredDestination && (
                <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  <div className="font-semibold">{dialogCopy.missingConfigTitle}</div>
                  <div className="mt-1">{dialogCopy.missingConfigBody}</div>
                </div>
              )}
            </div>
          )}

          {(invoice.status === "PENDING_VERIFICATION" || 
            invoice.status === "PENDING_HOA_TRANSFER" || 
            invoice.status === "PAID") && (
            <CommissionBreakdown invoice={invoice} />
          )}

          {(isDirectFlow || canPay) && residentPaymentDetails && (
            <PaymentDetailsCard
              details={residentPaymentDetails}
              title={t("payments.paymentInstructions")}
              description={
                isDirectFlow
                  ? dialogCopy.destinationDescription
                  : t("payments.sendPaymentToAccount")
              }
              showVerificationBadge={false}
            />
          )}

          {/* Action Buttons */}
          {step === "summary" && (
            <div className="flex flex-col sm:flex-row gap-3 pt-6 w-full border-t border-gray-200">
              <Button
                variant="outline"
                className="w-full sm:w-auto h-12 rounded-xl text-base font-bold border-2 border-gray-400 text-gray-800 hover:bg-gray-100 hover:border-gray-500 flex-1 shadow-sm transition-all"
                onClick={async () => {
                  try {
                    const blob = await downloadInvoice(invoice.id);
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank');
                  } catch { toast.error(t("invoice.downloadError")); }
                }}
              >
                📄 {isPaidFlow ? t("invoice.downloadReceipt") : t("invoice.download")}
              </Button>
              {canPay && (
                <Button
                  className="w-full sm:w-auto h-12 rounded-xl text-base font-bold text-black bg-gray-200 hover:bg-gray-300 border border-gray-300 active:scale-[0.98] transition shadow-sm flex-2 sm:flex-[2]"
                  disabled={!canStartPayment}
                  onClick={() => setStep("pay")}
                >
                  {t("payments.payNow")}
                </Button>
              )}
            </div>
          )}

          {isPendingVerification && (
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-3 text-sm text-sky-800 bg-sky-50 px-4 py-3 border border-sky-100 rounded-xl">
                <AlertCircle className="w-6 h-6 shrink-0" />
                <p className="font-medium">
                  {isDirectFlow
                    ? dialogCopy.hoaReviewPending
                    : `${t("payments.verificationPending")} ${t("payments.receiveConfirmationSoon")}`}
                </p>
              </div>
            </div>
          )}

          {state.key === "PENDING_HOA_TRANSFER" && (
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-3 text-sm text-blue-800 bg-blue-50 px-4 py-3 border border-blue-100 rounded-xl">
                <CheckCircle2 className="w-6 h-6 shrink-0" />
                <p className="font-medium">
                  {t("payments.verified")} - {t("payments.transferringToHoa")}
                </p>
              </div>
            </div>
          )}

          {isPaidFlow && (
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <div className="text-sm font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5" />
                {t("payments.invoiceAlreadyPaid")}
              </div>
            </div>
          )}

          {/* Payment flow */}
          {step === "pay" && canPay && canStartPayment && (
            <div className="mt-8 pt-6 border-t border-gray-200 space-y-6 animate-in slide-in-from-bottom-4 fade-in duration-300">
              <div className="space-y-4">
                <div className="text-base font-bold text-gray-900">
                  {t("payments.choosePaymentMethod")}
                </div>
                <Tabs
                  value={tab}
                  onValueChange={(v) => setTab(v as PaymentMethod)}
                  className="w-full"
                >
                  <TabsList className="flex flex-wrap h-auto w-full gap-1.5 rounded-2xl p-1.5 bg-gray-200/50">
                    {availablePaymentMethods.map((method) => (
                      <TabsTrigger
                        key={method}
                        value={method}
                        className="flex-1 min-w-[90px] rounded-xl data-[state=active]:bg-white data-[state=active]:text-[#1F3A93] data-[state=active]:font-bold data-[state=active]:shadow-md px-3 py-2 text-sm text-gray-600 font-medium transition-all"
                      >
                        {getPaymentMethodLabel(method, t)}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>

                <div className="bg-slate-50 border border-slate-200/60 rounded-2xl shadow-sm overflow-hidden">
                  <div className="bg-white px-5 py-3 border-b border-slate-200/60 flex items-center gap-3">
                    <div className="bg-slate-100 p-1.5 rounded-lg border border-slate-200/50">
                      <Info className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="text-sm font-semibold text-slate-800">
                      {t("payments.paymentAccountDetails")}
                    </span>
                  </div>
                  <div className="p-5 flex flex-col gap-5">
                    <div className="flex flex-col gap-4">
                      {accountLines.map(({ k, v }) => (
                        <CopyableField key={`${tab}-${k}`} label={k} value={v} />
                      ))}
                    </div>
                    <div className="bg-amber-50/50 border border-amber-100/50 rounded-xl p-3.5 text-sm text-amber-800 leading-relaxed">
                      {dialogCopy.referenceHelper}
                    </div>
                  </div>
                </div>
              </div>

              <form className="space-y-5" onSubmit={submitProof}>
                  <div className="text-base font-bold text-gray-900 pb-2 border-b border-gray-100">
                    {t("payments.uploadPaymentProof")}
                  </div>

                  <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    {dialogCopy.paymentProofHelp}
                  </div>

                  <div className="space-y-4">
                    {/* The rate this payment is recorded at comes from this
                        date. Defaulting to today keeps the common case a
                        single tap, while a resident uploading a proof days
                        after the transfer can now say so. */}
                    <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
                      <label className="text-sm font-semibold text-gray-800 block mb-2">
                        {t("payments.paymentDate")}
                      </label>
                      <input
                        type="date"
                        value={paidOn}
                        max={todayInCaracas()}
                        onChange={(e) => setPaidOn(e.target.value)}
                        className="w-full sm:w-56 px-3 py-2.5 rounded-xl border border-gray-300 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#1F3A93]/30 focus:border-[#1F3A93]"
                      />
                      <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                        {t("payments.paymentDateHelp")}
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
                      <label className="text-sm font-semibold text-gray-800 block mb-2">
                        {t("payments.transactionScreenshot")}
                      </label>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <label className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#1F3A93] bg-gray-50 hover:bg-[#1F3A93]/5 cursor-pointer text-sm font-medium text-gray-700 transition w-full sm:w-auto shrink-0">
                          <FileUp className="w-4 h-4 text-[#1F3A93]" />
                          <span>{t("payments.choosePdfJpgPng")}</span>
                          <input
                            type="file"
                            className="hidden"
                            accept="application/pdf,image/png,image/jpeg"
                            onChange={handleTransactionFileChange}
                          />
                        </label>
                        {transactionFile ? (
                          <div className="text-sm font-semibold text-[#1F3A93] truncate bg-blue-50 px-3 py-2 rounded-lg break-all">
                            ✓ {transactionFile.name}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400 italic">{t("payments.noFileChosen")}</div>
                        )}
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
                      <label className="text-sm font-semibold text-gray-800 block mb-2">
                        {t("payments.idDocument")}
                      </label>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <label className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#1F3A93] bg-gray-50 hover:bg-[#1F3A93]/5 cursor-pointer text-sm font-medium text-gray-700 transition w-full sm:w-auto shrink-0">
                          <FileUp className="w-4 h-4 text-[#1F3A93]" />
                          <span>{t("payments.choosePdfJpgPng")}</span>
                          <input
                            type="file"
                            className="hidden"
                            accept="application/pdf,image/png,image/jpeg"
                            onChange={handleIdFileChange}
                          />
                        </label>
                        {idFile ? (
                          <div className="text-sm font-semibold text-[#1F3A93] truncate bg-blue-50 px-3 py-2 rounded-lg break-all">
                            ✓ {idFile.name}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400 italic">{t("payments.noFileChosen")}</div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-800 block mb-2">
                        {t("payments.notesOptional")}
                      </label>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        placeholder={t("payments.addNoteAboutPayment")}
                        className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A93] focus:border-transparent transition resize-none"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="text-sm font-medium text-rose-600 bg-rose-50 p-3 rounded-xl border border-rose-200">
                      ⚠ {error}
                    </div>
                  )}

                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-6 mt-6 border-t border-gray-200 w-full">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      className="w-full sm:w-auto h-12 rounded-xl text-base font-bold bg-white text-gray-800 border-2 border-gray-400 hover:bg-gray-100 hover:border-gray-500 shadow-sm transition-all flex-1"
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      className="w-full sm:w-auto h-12 rounded-xl text-base font-bold text-black bg-gray-200 hover:bg-gray-300 border border-gray-300 shadow-sm flex-[2] transition active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100 flex items-center justify-center gap-2"
                      disabled={
                        !transactionFile ||
                        !idFile ||
                        !paidOn ||
                        markAsPaidMutation.isPending
                      }
                    >
                      {markAsPaidMutation.isPending ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : null}
                      {markAsPaidMutation.isPending
                        ? t("payments.sending")
                        : t("payments.markAsPaid")}
                    </Button>
                  </div>
                </form>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CopyableField({ label, value }: { label: string; value: string }) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</span>
      <div className="group flex items-center justify-between gap-3 bg-white border border-gray-200 px-3 py-2.5 rounded-xl shadow-sm hover:border-[#1F3A93]/30 transition-colors">
        <span className="text-sm font-medium text-gray-900 break-all">{value}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-[#1F3A93] hover:bg-[#1F3A93]/10 focus:outline-none transition-colors"
          title={t("payments.copy")}
        >
          {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
